import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { KeyRound, Loader2, Eye, EyeOff } from "lucide-react";

export default function ChangePasswordDialog({ open, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConf, setShowConf] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  function reset() {
    setCurrent(""); setNext(""); setConfirm("");
    setShowCur(false); setShowNew(false); setShowConf(false); setErr(""); setBusy(false);
  }

  function close() { reset(); onClose(); }

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (next.length < 6) {
      setErr("New password must be at least 6 characters.");
      return;
    }
    if (next !== confirm) {
      setErr("New password and confirmation do not match.");
      return;
    }
    if (next === current) {
      setErr("New password must be different from your current password.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed successfully");
      close();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Failed to change password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent
        data-testid="change-password-dialog"
        className="bg-[#121318] border-[#27272A] text-white max-w-md rounded-sm"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#A1A1AA]">
            <KeyRound className="h-3.5 w-3.5 text-[#007AFF]" />
            Change Password
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <PasswordField
            label="Current Password"
            value={current}
            onChange={setCurrent}
            show={showCur}
            onToggle={() => setShowCur((s) => !s)}
            testid="cp-current"
            autoFocus
          />
          <PasswordField
            label="New Password"
            hint="At least 6 characters"
            value={next}
            onChange={setNext}
            show={showNew}
            onToggle={() => setShowNew((s) => !s)}
            testid="cp-new"
          />
          <PasswordField
            label="Confirm New Password"
            value={confirm}
            onChange={setConfirm}
            show={showConf}
            onToggle={() => setShowConf((s) => !s)}
            testid="cp-confirm"
          />

          {err && (
            <div data-testid="cp-error" className="text-xs text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/20 px-3 py-2 rounded-sm">
              {err}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={close}
              disabled={busy}
              data-testid="cp-cancel"
              className="bg-transparent border-[#27272A] text-[#A1A1AA] hover:bg-[#1A1B23] hover:text-white rounded-sm"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy || !current || !next || !confirm}
              data-testid="cp-submit"
              className="bg-[#007AFF] hover:bg-[#007AFF]/90 text-white rounded-sm"
            >
              {busy ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
                  Updating…
                </>
              ) : (
                "Change Password"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PasswordField({ label, hint, value, onChange, show, onToggle, testid, autoFocus }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-[10px] tracking-[0.25em] uppercase text-[#71717A]">{label}</Label>
        {hint && <span className="text-[10px] text-[#71717A]">{hint}</span>}
      </div>
      <div className="relative mt-2">
        <Input
          data-testid={testid}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoFocus={autoFocus}
          className="bg-[#08080A] border-[#27272A] text-white rounded-sm h-10 pr-10"
        />
        <button
          type="button"
          onClick={onToggle}
          data-testid={`${testid}-toggle`}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center text-[#71717A] hover:text-white rounded-sm"
          tabIndex={-1}
        >
          {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}
